import {AttributesRow, Flag} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {getCustomerName, useStateFlag} from '@dms/shared';

import {buildArray, parseDate} from 'shared/web';

import {usePairedServiceOrder} from '../../../hooks/usePairedServiceOrder';

export const useGetServiceOrderRows = (rentalOrderId: string) => {
  const formatDateTime = useDateTimeFormatter();
  const formatCurrency = useFormatCurrency();

  const {
    serviceOrder,
    serviceOrderVariant,
    serviceOrderTypes,
    servicedVehicle,
    vehicleOperatorCustomer,
  } = usePairedServiceOrder(rentalOrderId);

  const stateFlagProps = useStateFlag(serviceOrder?.state);

  const formatPrice = (
    price:
      | {
          amount?: number | null;
          currency?: string | null;
        }
      | null
      | undefined
  ) => {
    if (isNotNil(price?.amount) && isNotNil(price?.currency)) {
      return formatCurrency(price.amount, price.currency);
    }

    return null;
  };

  const serviceOrderRows: AttributesRow[] = buildArray<AttributesRow>().when(
    isNotNil(serviceOrder),
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderState'),
      content: <Flag label={stateFlagProps?.label ?? ''} {...stateFlagProps} size="small" />,
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderType'),
      value: serviceOrderTypes?.find((item) => item?.id === serviceOrderVariant?.serviceOrderTypeId)
        ?.name,
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderVariant'),
      value: serviceOrderVariant?.name,
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderCompletionDate'),
      value: isNotNil(serviceOrder?.completionAt)
        ? formatDateTime('dateShort', parseDate(serviceOrder.completionAt))
        : null,
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderEstimatedPrice'),
      value: formatPrice(serviceOrder?.totalPriceEstimated),
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderTotalPriceWithVat'),
      value: formatPrice(serviceOrder?.totalPrice?.withVat),
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderTotalPriceWithoutVat'),
      value: formatPrice(serviceOrder?.totalPrice?.withoutVat),
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderVehicle'),
      value: servicedVehicle?.title,
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrderVehicleOperator'),
      value: getCustomerName(vehicleOperatorCustomer),
    }
  );

  return serviceOrderRows;
};
